import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { SEO, BreadcrumbStructuredData } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { useCart } from '@/context/CartContext';
import { ProductReviews } from '@/components/ProductReviews';
import ProductUpsell from '@/components/ProductUpsell';
import { FactoryTrustBadge } from '@/components/classic';
import { RealWorkspaceSection } from '@/components/RealWorkspaceSection';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart } from 'lucide-react';

// Import refactored components
import {
  ProductGallery,
  ProductPricing,
  ProductQuantity,
  ProductAccessories,
  ProductSpecifications,
  MEETING_BOOTH_LARGE_ACCESSORIES
} from '@/components/product';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProductDetail() {
  const { id, slug } = useParams();
  const productIdOrSlug = slug || id;
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToCart } = useCart();
  
  // Accessories state for Meeting Booth Large
  const [benchQuantity, setBenchQuantity] = useState(0);
  const [tableQuantity, setTableQuantity] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [productIdOrSlug]);

  // Inject Product Schema
  useEffect(() => {
    if (!product) return;

    const existingSchema = document.getElementById('product-schema');
    if (existingSchema) existingSchema.remove();

    const priceValidDate = new Date();
    priceValidDate.setDate(priceValidDate.getDate() + 30);

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `https://www.modularfurniturestore.com/product/${product.slug || product.id}#product`,
      "name": product.name,
      "description": product.description,
      "image": product.image_urls || [product.image_url],
      "sku": product.id,
      "mpn": product.id?.substring(0, 12),
      "brand": { "@type": "Brand", "name": "Modular Furniture Store" },
      "category": product.category,
      "offers": {
        "@type": "Offer",
        "url": `https://www.modularfurniturestore.com/product/${product.slug || product.id}`,
        "priceCurrency": "INR",
        "price": product.price,
        "priceValidUntil": priceValidDate.toISOString().split('T')[0],
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition",
        "seller": { "@type": "Organization", "name": "Modular Furniture Store" }
      }
    };

    const script = document.createElement('script');
    script.id = 'product-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(productSchema);
    document.head.appendChild(script);

    return () => {
      const schemaToRemove = document.getElementById('product-schema');
      if (schemaToRemove) schemaToRemove.remove();
    };
  }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/${productIdOrSlug}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    
    // Add accessories for Meeting Booth Large
    if (product.subcategory === 'Meeting Booth Large') {
      if (benchQuantity > 0) {
        const benchProduct = {
          id: `${product.id}-bench`,
          name: `Seating Bench (for ${product.name})`,
          price: MEETING_BOOTH_LARGE_ACCESSORIES.seatingBench.price,
          mrp: MEETING_BOOTH_LARGE_ACCESSORIES.seatingBench.mrp,
          image_url: product.image_url,
          category: 'Accessory',
          subcategory: 'Seating Bench'
        };
        addToCart(benchProduct, benchQuantity);
      }
      
      if (tableQuantity > 0) {
        const tableProduct = {
          id: `${product.id}-table`,
          name: `Table (for ${product.name})`,
          price: MEETING_BOOTH_LARGE_ACCESSORIES.table.price,
          mrp: MEETING_BOOTH_LARGE_ACCESSORIES.table.mrp,
          image_url: product.image_url,
          category: 'Accessory',
          subcategory: 'Meeting Table'
        };
        addToCart(tableProduct, tableQuantity);
      }
    }
    
    let message = `${quantity} x ${product.name} added to cart!`;
    if (product.subcategory === 'Meeting Booth Large') {
      if (benchQuantity > 0) message += ` + ${benchQuantity} Bench(es)`;
      if (tableQuantity > 0) message += ` + ${tableQuantity} Table(s)`;
    }
    toast.success(message);
  };

  const calculateTotalPrice = () => {
    let total = product.price * quantity;
    if (product.subcategory === 'Meeting Booth Large') {
      total += MEETING_BOOTH_LARGE_ACCESSORIES.seatingBench.price * benchQuantity;
      total += MEETING_BOOTH_LARGE_ACCESSORIES.table.price * tableQuantity;
    }
    return total;
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-texture">
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-20 text-center">
          <p className="text-xl uppercase tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen noise-texture">
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-20 text-center">
          <p className="text-xl uppercase tracking-widest mb-8">PRODUCT NOT FOUND</p>
          <Link to="/products">
            <Button className="rounded-none uppercase tracking-wider font-bold">
              BACK TO PRODUCTS
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-texture">
      <SEO 
        title={`${product.name} | ${product.category === 'Modular Office Workstation' ? 'Workstation' : product.category === 'Privacy Booths' ? 'Booth' : 'Storage'} | MFS`}
        description={`Buy ${product.name} online from MFS. Factory-direct pricing. ${product.category === 'Modular Office Workstation' ? 'Modular workstation with cable management & ergonomic design.' : product.category === 'Privacy Booths' ? 'Soundproof booth with LED, ventilation & USB charging.' : 'Premium storage with lock system & adjustable shelves.'} Free shipping Mumbai.`}
        keywords={`${product.name}, ${product.category}, modular furniture, office furniture mumbai`}
        canonical={`/product/${product.slug || product.id}`}
        ogImage={product.image_url}
        ogType="product"
      />
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Visual Breadcrumbs */}
        <Breadcrumbs items={[
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/products' },
          { name: product.category, url: `/products?category=${encodeURIComponent(product.category)}` },
          { name: product.name }
        ]} />
        <BreadcrumbStructuredData items={[
          { name: 'Home', url: '/' },
          { name: 'Products', url: '/products' },
          { name: product.category, url: `/products?category=${encodeURIComponent(product.category)}` },
          { name: product.name }
        ]} />

        {/* Back Button */}
        <Link to={`/products?category=${encodeURIComponent(product.category)}`} data-testid="back-to-products">
          <Button variant="ghost" className="rounded-none uppercase tracking-wider font-bold mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            BACK TO {product.category?.toUpperCase() || 'PRODUCTS'}
          </Button>
        </Link>

        {/* Product Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <ProductGallery 
            product={product}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
          />

          {/* Product Info */}
          <div className="space-y-6">
            {/* Title & Description */}
            <div>
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">
                {product.category} / {product.subcategory}
              </p>
              <h1 className="text-4xl md:text-5xl font-black uppercase mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-lg text-muted-foreground">
                {product.description}
              </p>
            </div>

            {/* Dimensions */}
            <div className="border-t border-b border-border py-6">
              <p className="text-sm uppercase tracking-widest font-bold mb-2">DIMENSIONS</p>
              <p className="mono text-lg" data-testid="product-dimensions">
                {typeof product.dimensions === 'string' 
                  ? product.dimensions 
                  : product.dimensions?.size || ''}
              </p>
            </div>

            {/* Pricing */}
            <ProductPricing product={product} />

            {/* Quantity Selector */}
            <ProductQuantity quantity={quantity} setQuantity={setQuantity} />

            {/* Optional Accessories for Meeting Booth Large */}
            {product.subcategory === 'Meeting Booth Large' && (
              <ProductAccessories
                benchQuantity={benchQuantity}
                setBenchQuantity={setBenchQuantity}
                tableQuantity={tableQuantity}
                setTableQuantity={setTableQuantity}
                calculateTotalPrice={calculateTotalPrice}
              />
            )}

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              data-testid="add-to-cart-detail"
              className="w-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold px-12 py-6 text-lg"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              ADD TO CART
            </Button>

            {/* Factory Trust Badge */}
            <FactoryTrustBadge />
          </div>
        </div>

        {/* Product Specifications */}
        <ProductSpecifications specifications={product.specifications} />

        {/* Real Workspace Section - Only for Privacy Booths */}
        <RealWorkspaceSection category={product.category} />

        {/* Product Reviews */}
        <ProductReviews productId={product.id} productName={product.name} />

        {/* Upsell Recommendations */}
        <ProductUpsell currentCategory={product.category} currentProductId={product.id} />
      </div>
    </div>
  );
}
